/**
 * WidgetState — wspólne stany pomocnicze kafelków z danymi (ładowanie / pusto /
 * brak firmy / brak uprawnienia / błąd). Trzyma jednolity wygląd i pełne
 * wyśrodkowanie w obszarze treści kafelka.
 */

import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/Spinner';

import type { DataWidgetErrorKind } from '../dashboard-api';

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 px-2 text-center">
      {children}
    </div>
  );
}

export function WidgetLoading() {
  return (
    <Centered>
      <Spinner />
    </Centered>
  );
}

export function WidgetEmpty({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <Centered>
      <span className="text-xs text-text-tertiary">{message ?? t('tenant.dashboard.empty')}</span>
    </Centered>
  );
}

/** Łagodny stan dla 400/403 (brak firmy / brak uprawnienia) + retry dla błędu. */
export function WidgetDataError({
  kind,
  onRetry,
}: {
  kind: DataWidgetErrorKind;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  if (kind === 'no-company') return <WidgetEmpty message={t('tenant.dashboard.noCompany')} />;
  if (kind === 'permission')
    return <WidgetEmpty message={t('tenant.dashboard.permissionDenied')} />;
  return (
    <Centered>
      <span className="text-xs text-text-secondary">{t('tenant.dashboard.widgetError')}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-xs font-medium text-accent-600 hover:text-accent-700"
        >
          {t('common.retry')}
        </button>
      )}
    </Centered>
  );
}
